import React, { useRef, useEffect, memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import useCaseMasterStore from './CaseMasterStore';
import { CasemasterAssessmentStates } from './constant';

const useStyles = makeStyles((theme) => ({
  timerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  timeBox: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '60px',
  },
  warningTimeBox: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #F59E0B',
  },
  dangerTimeBox: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #DC2626',
  },
  colon: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#000000',
    margin: `0 ${theme.spacing(0.5)}`,
  },
  displayTimer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.neutral['clr-100'],
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200']
  },
  timeIcon: {
    fontSize: '16px',
  },
  timeIconDanger: {
    color: '#DC2626',
  },
  timeIconNormal: {
    color: '#666',
  }
}));

// Moved outside component to avoid recreation on each render
const formatTime = (seconds) => {
  // Handle undefined, null, or NaN values
  if (seconds === undefined || seconds === null || isNaN(seconds)) {
    return {
      minutes: '00',
      seconds: '00'
    };
  }

  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return {
    minutes: minutes.toString().padStart(2, '0'),
    seconds: secs.toString().padStart(2, '0')
  };
};

export const DisplayTimer = memo(({ timeLeft, warningTime = 60 }) => {
  const classes = useStyles();
  const time = formatTime(timeLeft || 0);

  const timerStyle = useMemo(() => {
    if (timeLeft <= 30) return { borderColor: '#DC2626' };
    if (timeLeft <= warningTime) return { borderColor: '#F59E0B' };
    return {};
  }, [timeLeft, warningTime]);

  const isDanger = timeLeft <= 30;

  return (
    <Box className={classes.displayTimer} sx={timerStyle}>
      <AccessTimeIcon className={`${classes.timeIcon} ${isDanger ? classes.timeIconDanger : classes.timeIconNormal}`} />
      <Typography
        variant="body02-bold"
        color={isDanger ? 'danger.clr-600' : 'neutral.clr-700'}
      >
        {`${time.minutes}:${time.seconds}`}
      </Typography>
    </Box>
  );
});

const Timer = ({ totalTime = 0, warningTime = 0 }) => {
  const classes = useStyles();

  const timeLeft = useCaseMasterStore(state => state.timeLeft);
  const setTimeLeft = useCaseMasterStore(state => state.setTimeLeft);
  const setTimeOut = useCaseMasterStore(state => state.setTimeOut);
  const setAssessmentState = useCaseMasterStore(state => state.setAssessmentState);
  const assessmentState = useCaseMasterStore(state => state.assessmentState);

  const timeLeftRef = useRef(timeLeft);

  useEffect(() => {
    setTimeLeft(totalTime);
  }, [totalTime, setTimeLeft]);

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  useEffect(() => {
    // Only start countdown when assessment is RUNNING
    if (assessmentState !== CasemasterAssessmentStates.RUNNING) {
      return;
    }

    const timer = setInterval(() => {
      let newTime = timeLeftRef.current;

      if (newTime <= 1) {
        clearInterval(timer);
        newTime = 0;
        console.log('Timer reached 0, finishing assessment...');
        setTimeOut(true);
        setAssessmentState(CasemasterAssessmentStates.FINISHING);
      } else {
        newTime = newTime - 1;
      }

      setTimeLeft(newTime);
    }, 1000);

    return () => clearInterval(timer);
  }, [setTimeLeft, setTimeOut, setAssessmentState, assessmentState]);

  const timerClass = useMemo(() => {
    if (timeLeft <= 60) return classes.dangerTimeBox;
    if (timeLeft <= 300) return classes.warningTimeBox;
    return '';
  }, [timeLeft, classes.dangerTimeBox, classes.warningTimeBox]);

  const time = formatTime(timeLeft || 0);

  return (
    <Box className={classes.timerContainer}>
      <Box className={`${classes.timeBox} ${timerClass}`}>
        <Typography
          variant="h6-medium"
          color='neutral.clr-900'
        >
          {time.minutes}
        </Typography>
      </Box>
      <Typography className={classes.colon}>:</Typography>
      <Box className={`${classes.timeBox} ${timerClass}`}>
        <Typography
          variant="h6-medium"
          color='neutral.clr-900'
        >
          {time.seconds}
        </Typography>
      </Box>
    </Box>
  );
};

export default Timer;

